import { DeleteOutlined, MailOutlined, TeamOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { EnterIcon } from 'assets/images/svg';
import SuperFetch from 'library/helpers/superFetch';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { Fragment, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { Notification, WarningAlert } from 'zComponents/atoms';
import { Avatar, Button, Col, Flex, Form, Input, List, Modal, Row, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Title, Text } = Typography;
const InvitePeopleToTeamModal = ({ open, setOpen, children, existingPeople = [], onInvite }) => {
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingLG,
			paddingXL,
			paddingMD,
			paddingXS,
			colorTextDescription,
			fontWeightStrong,
			colorText,
			paddingSM
		}
	} = theme.useToken();

	const [form] = Form.useForm();
	const { loadingAddMembersTeam } = useSelector((s) => s.Teams);
	const { loadingAddProfile } = useSelector((s) => s.People);
	const peopleData = useSelector(selectPeople);

	// internal vs. controlled open
	const [internalVisible, setInternalVisible] = useState(false);
	const [warningMessage, setWarningMessage] = useState('');
	const [isWarning, setIsWarning] = useState(false);
	const [loader, setLoader] = useState(false);
	const visible = open ?? internalVisible;
	const setVisible = setOpen ?? setInternalVisible;

	// local list of pending emails (strings)
	const [selectedPeople, setSelectedPeople] = useState([]);

	// Close + reset everything
	const resetAll = useCallback(() => {
		setSelectedPeople([]);
		form.resetFields();
		setVisible(false);
	}, [form, setVisible]);

	const handleAddUserByEmail = async () => {
		try {
			const { email } = await form.validateFields();
			const entries = email
				.split(',')
				.map((e) => e?.trim()?.toLowerCase())
				.filter((e) => e);
			const uniqueEntries = Array.from(new Set(entries));

			const newEntries = [];
			uniqueEntries.forEach((addr) => {
				// skip if already in existingPeople
				if (existingPeople?.some((p) => p?.label?.toLowerCase() === addr)) {
					setWarningMessage(`${addr} is already in the team.`);
					setIsWarning(true);
				}
				// skip if already in selected list
				else if (selectedPeople?.includes(addr)) {
					setWarningMessage(`${addr} has already been added to the invite list.`);
					setIsWarning(true);
				} else if (peopleData?.some((p) => p?.email?.toLowerCase() === addr)) {
					setWarningMessage(`${addr} already exists.`);
					setIsWarning(true);
				} else {
					newEntries.push(addr);
					setWarningMessage('');
					setIsWarning(false);
				}
			});

			if (newEntries.length) {
				setSelectedPeople((prev) => [...prev, ...newEntries]);
			}

			form.resetFields();
		} catch (err) {
			console.error('Validation failed:', err);
		}
	};

	const handleAddPeopleToTeam = async () => {
		if (!selectedPeople.length) return;

		// 1) De-duplicate & skip existing
		const toInvite = [];
		selectedPeople.forEach((email) => {
			const lower = email.toLowerCase();
			if (existingPeople.some((p) => p.label.toLowerCase() === lower)) {
				Notification('warning', 'Already Exists', `${email} is already in the team.`);
			} else if (toInvite.includes(lower)) {
				Notification('warning', 'Duplicate Entry', `${email} is duplicated in invite list.`);
			} else {
				toInvite.push(email);
			}
		});
		if (!toInvite.length) return;

		try {
			setLoader(true);

			// 2) POST invites
			const responses = await Promise.all(toInvite.map((email) => SuperFetch.post('/people', { email })));

			// 3) Extract personIDs & validate
			const personIDs = responses.map((res) => {
				if (![200, 201].includes(res.statusCode)) {
					throw new Error(`Invite failed for ${res.data?.email || 'unknown'}`);
				}
				return res.data.personID;
			});
			onInvite(personIDs);
			Notification('success', 'Invited', `Added ${personIDs.length} person(s) successfully.`);
		} catch (err) {
			console.error(err);
			Notification('error', 'Invite Error', err.message || 'Something went wrong');
		} finally {
			setLoader(false);
			resetAll();
		}
	};

	const handleClearList = () => setSelectedPeople([]);
	const handleDeleteUser = (email) => {
		setSelectedPeople((prev) => prev.filter((e) => e !== email));
	};

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText='Invite People'
				condition={false}
				disabledCondition={false}
				trueTitle="You don't have permission"
				falseTitle='Invite people to this team'
				onClick={() => setVisible(true)}
				icon={<TeamOutlined />}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				open={visible}
				onCancel={resetAll}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='flex-end'
						style={{ marginTop: marginLG }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={resetAll} type='default' disabled={loadingAddMembersTeam}>
							Cancel
						</Button>
						<Button
							onClick={handleAddPeopleToTeam}
							type='primary'
							loading={loadingAddMembersTeam || loadingAddProfile || loader}
							disabled={selectedPeople.length === 0}
						>
							Invite People
						</Button>
					</Flex>
				]}
				width={472}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				{/* Header */}
				<Row gutter={[0, paddingLG]}>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Add People to Team
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
							Select the people to add to this team.
						</Text>
					</Col>

					{/* Email input + local "↵" button */}
					<Col span={24}>
						<Form form={form} layout='vertical'>
							<Form.Item
								name='email'
								label={<Text strong>Invite Email Addresses</Text>}
								rules={fieldsValidator.MultiEmail}
								required={false}
							>
								<Row gutter={8}>
									<Col flex='auto'>
										<Input
											placeholder='user@workspace.com, user2@workspace.com'
											prefix={<MailOutlined />}
											onPressEnter={handleAddUserByEmail}
										/>
									</Col>
									<Col>
										<Button type='primary' icon={<EnterIcon />} onClick={handleAddUserByEmail} />
									</Col>
								</Row>
							</Form.Item>
							{isWarning && <WarningAlert message={warningMessage} />}
						</Form>
					</Col>

					{/* Local list of emails with avatar and email */}
					<Col span={24}>
						<Flex vertical gap={paddingMD}>
							<Flex align='center' justify='space-between'>
								<Text strong>Selected People</Text>
								<Button type='text' onClick={handleClearList} style={{ textDecoration: 'underline' }}>
									Clear List
								</Button>
							</Flex>

							<List
								dataSource={selectedPeople}
								renderItem={(email) => (
									<List.Item
										style={{ padding: 0, border: 0, marginBottom: paddingXS }}
										extra={
											<Button
												icon={<DeleteOutlined style={{ fontSize: 16 }} />}
												onClick={() => handleDeleteUser(email)}
												type='text'
											/>
										}
									>
										<List.Item.Meta
											avatar={
												<Avatar
													size={30}
													style={{
														fontSize: fontSizeSM,
														color: colorText,
														fontWeight: fontWeightStrong
													}}
												>
													{email.charAt(0).toUpperCase()}
												</Avatar>
											}
											title={<Text style={{ fontWeight: 400 }}>{email}</Text>}
										/>
									</List.Item>
								)}
							/>
						</Flex>
					</Col>
				</Row>
			</Modal>
		</Fragment>
	);
};

export default InvitePeopleToTeamModal;
